import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface UserDetailPanelProps {
  user: {
    id: string;
    email: string;
    username: string | null;
    name: string | null;
    last_sign_in_at: string | null;
    created_at: string;
    isAdmin: boolean;
  };
  onClose: () => void;
}

export const UserDetailPanel = ({ user, onClose }: UserDetailPanelProps) => {
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [examSpecs, setExamSpecs] = useState<any[]>([]);
  const [gameSpecs, setGameSpecs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch activity logs
      const { data: logs, error: logsError } = await supabase
        .from('user_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (logsError) {
        console.error("Error fetching activity logs:", logsError);
        setError("Failed to load activity logs");
        toast({
          title: "Error loading activity logs",
          description: logsError.message,
          variant: "destructive"
        });
      } else {
        setActivityLogs(logs || []);
      }

      // Fetch exam attempts and results
      const { data: examAttempts, error: examAttemptsError } = await supabase
        .from('exam_attempts')
        .select(`
          *,
          results:exam_results!exam_attempts_id_fkey(
            id,
            score,
            completed_at,
            questions_answered,
            correct_answers
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (examAttemptsError) {
        console.error("Error fetching exam attempts:", examAttemptsError);
        setError("Failed to load exam attempts");
        toast({
          title: "Error loading exam attempts",
          description: examAttemptsError.message,
          variant: "destructive"
        });
      } else {
        setExamSpecs(examAttempts || []);
      }

      // Fetch user achievements
      const { data: achievements, error: achievementsError } = await supabase
        .from('user_achievements')
        .select(`
          *,
          achievement:achievements(id, title, description, visual_asset)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (achievementsError) {
        console.error("Error fetching achievements:", achievementsError);
        setError("Failed to load achievements");
        toast({
          title: "Error loading achievements",
          description: achievementsError.message,
          variant: "destructive"
        });
      } else {
        setGameSpecs(achievements || []);
      }

      // Fetch user sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false });

      if (sessionsError) {
        console.error("Error fetching user sessions:", sessionsError);
        setError("Failed to load user sessions");
        toast({
          title: "Error loading user sessions",
          description: sessionsError.message,
          variant: "destructive"
        });
      } else {
        setGameSpecs(sessions || []);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError("Failed to load user details");
      toast({
        title: "Error loading user details",
        description: err instanceof Error ? err.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user.id]);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}`}
                alt={user.name || user.email}
              />
              <AvatarFallback>
                {user.name
                  ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
                  : user.email?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-semibold">{user.name || user.email}</h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Error Loading Details</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}`}
              alt={user.name || user.email}
            />
            <AvatarFallback>
              {user.name
                ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
                : user.email?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-2xl font-semibold">{user.name || user.email}</h2>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
        <Button variant="ghost" onClick={onClose}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-6">
          <Card>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="exam-specs">Exam Specs</TabsTrigger>
            <TabsTrigger value="game-specs">Game Specs</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Username</p>
                      <p className="text-lg font-semibold">{user.username || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-lg font-semibold">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Role</p>
                      <p className="text-lg font-semibold">{user.isAdmin ? "Admin" : "User"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Joined</p>
                      <p className="text-lg font-semibold">
                        {format(new Date(user.created_at), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {activityLogs.map((log, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium">{log.action}</p>
                          <p className="text-sm text-gray-500">{log.details}</p>
                        </div>
                        <span className="text-sm text-gray-400">
                          {format(new Date(log.created_at), "HH:mm")}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="exam-specs">
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Exam History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {examSpecs.map((attempt, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <h3 className="font-semibold">Exam {index + 1}</h3>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-500">Score</p>
                            <p className="font-medium">{attempt.results?.score}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Questions</p>
                            <p className="font-medium">
                              {attempt.results?.questions_answered} / {attempt.results?.correct_answers}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Completed</p>
                            <p className="font-medium">
                              {format(new Date(attempt.results?.completed_at), "MMM d, yyyy HH:mm")}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="font-medium">{attempt.duration} minutes</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="game-specs">
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Achievements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {gameSpecs.map((achievement, index) => (
                      <div key={index} className="border rounded-lg p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <img
                            src={achievement.achievement?.visual_asset}
                            alt={achievement.achievement?.title}
                            className="w-8 h-8"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">{achievement.achievement?.title}</h3>
                          <p className="text-sm text-gray-500">{achievement.achievement?.description}</p>
                          <p className="text-xs text-gray-400">
                            Earned on {format(new Date(achievement.created_at), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {gameSpecs.map((session, index) => (
                      <div key={index} className="border rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium">Session {index + 1}</p>
                          <p className="text-sm text-gray-500">
                            Duration: {session.duration_minutes} minutes
                          </p>
                        </div>
                        <span className="text-sm text-gray-400">
                          {format(new Date(session.started_at), "HH:mm")}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default UserDetailPanel;
